import React, { useState, useEffect } from 'react';
import {
  Box, Button, Grid, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, Typography,
  TextField, Chip, Autocomplete, Tooltip, useMediaQuery, useTheme,
  Snackbar, Alert
} from '@mui/material';
import { Add, Save, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import DynamicTable from '../../Components/Dynamic/DynamicTable';
import DynamicOffcanvas from '../../Components/Dynamic/DynamicOffcanvas';
import { useUser } from '../../Context/UserContext';
import { GetApi, PostApi, PutApi, DeleteApi } from '../../Services/api/ApiCall';

const ProductionCenterScheme = () => {
  const { token, userId } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  const [centers, setCenters] = useState([]);
  const [allSchemes, setAllSchemes] = useState([]);
  const [availableSchemes, setAvailableSchemes] = useState([]);

  const [open, setOpen] = useState(false);

  const getInitialFormState = () => ({
    id: null,
    production_center_id: null,
    scheme_id: null
  });

  const [formData, setFormData] = useState(getInitialFormState());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast(prev => ({ ...prev, open: false }));
  };

  const showToast = (message, severity = "error") => {
    setToast({ open: true, message, severity });
  };

  const fetchDropdownData = async () => {
    try {
      const centerRes = await GetApi(`/center/production/?production_type=private`, token);
      const allCenters = Array.isArray(centerRes) ? centerRes : (centerRes?.results || []);
      setCenters(allCenters.filter(c => c.production_type === 'private'));

      const schemeRes = await GetApi('/center/schema/', token);
      const schemesList = Array.isArray(schemeRes) ? schemeRes : (schemeRes?.results || []);
      setAllSchemes(schemesList);
      
    } catch (err) {
      console.error("Error fetching dropdown options:", err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await GetApi('/center/productioncenter-scheme/', token);
      const data = Array.isArray(res) ? res : (res?.results || []);
      setTableData(data);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDropdownData();
      fetchData();
    }
  }, [token]);

  useEffect(() => {
    if (!formData.production_center_id || !formData.production_center_id.id) {
      setAvailableSchemes(allSchemes);
      return;
    }

    const selectedCenterId = formData.production_center_id.id;
    const assignedSchemeIds = tableData
      .filter(item => item.center_id === selectedCenterId && item.id !== formData.id)
      .map(item => item.scheme_id);

    const filtered = allSchemes.filter(scheme => !assignedSchemeIds.includes(scheme.id));
    setAvailableSchemes(filtered);
  }, [formData.production_center_id, tableData, allSchemes, formData.id]);

  useEffect(() => {
    if (open && !formData.id) {
      setFormData(getInitialFormState());
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    let newErrors = {};
    if (!formData.production_center_id) newErrors.production_center_id = "Production Center is required";
    if (!formData.scheme_id) newErrors.scheme_id = "Scheme is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'production_center_id') {
      setFormData(prev => ({ ...prev, scheme_id: null }));
    }
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);

    try {
      const payload = {
        production_center_id: formData.production_center_id.id,
        scheme_id: formData.scheme_id.id,
        created_by: userId
      };

      const payloadedit = {
        production_center_id: formData.production_center_id.id,
        scheme_id: formData.scheme_id.id,
        updated_by: userId
      };

      if (formData.id) {
        await PutApi(`/center/productioncenter-scheme/${formData.id}`, {}, payloadedit, token);
        showToast("Assignment updated successfully!", "success");
      } else {
        await PostApi('/center/productioncenter-scheme/', payload, token);
        showToast("Scheme assigned successfully!", "success");
      }

      setOpen(false);
      setFormData(getInitialFormState());
      fetchData();

    } catch (err) {
      console.error("Submit error:", err);
      
      // --- ROBUST ERROR EXTRACTOR ---
      // This handles both raw Axios errors AND generic string/Error throws from API wrappers
      let errorMsg = "Error assigning scheme.";
      
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err?.response?.data) {
        // Check standard Django/DRF error keys
        errorMsg = err.response.data.error 
                || err.response.data.message 
                || err.response.data.detail 
                || JSON.stringify(err.response.data);
      } else if (err?.message) {
        errorMsg = err.message;
      }
      
      showToast(errorMsg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (row) => {
    setFormData({
      id: row.id,
      production_center_id: { id: row.center_id, name_of_production_centre: row.center_name },
      scheme_id: { id: row.scheme_id, name: row.scheme_name }
    });
    setOpen(true);
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedRow) return;

    try {
      await DeleteApi(`/center/productioncenter-scheme/${selectedRow.id}`, token);
      showToast("Assignment deleted successfully!", "info");
      setDeleteOpen(false);
      setSelectedRow(null);
      fetchData();
    } catch (err) {
      console.error("Delete error:", err);
      
      let errorMsg = "Failed to delete assignment.";
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err?.response?.data) {
        errorMsg = err.response.data.error || err.response.data.message || err.response.data.detail || JSON.stringify(err.response.data);
      } else if (err?.message) {
        errorMsg = err.message;
      }
      
      showToast(errorMsg, "error");
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60, sx: { display: { xs: 'none', sm: 'table-cell' } } },
    { 
      field: 'center_name', headerName: 'Production Center', width: isMobile ? 200 : 250,
      render: (row) => (<Box><Typography variant="body2" fontWeight="bold">{row.center_name}</Typography></Box>)
    },
    { 
      field: 'scheme_name', headerName: 'Scheme', width: isMobile ? 150 : 200,
      render: (row) => <Chip label={row.scheme_name} size="small" color="success" variant="outlined" />
    },
    {
      field: 'created_by_name', headerName: 'Created By', width: 120,
      sx: { display: { xs: 'none', md: 'table-cell' } },
      render: (row) => (<Typography variant="caption" sx={{ fontStyle: 'italic' }}>{row.created_by_name || 'System'}</Typography>)
    }
  ];

  const goldButtonStyle = {
    bgcolor: '#095f2d', color: 'white', fontWeight: 'bold',
    '&:hover': { bgcolor: '#095f2d' }
  };

  const offcanvasWidth = isMobile || isTablet ? '100%' : '45%';

  return (
    <Box sx={{ backgroundColor: '#e8f7e0', minHeight: '100vh', p: { xs: 1, sm: 3 } }}>
      <Box display="flex" justifyContent="space-between" gap={2} flexDirection={{ xs: 'column', sm: 'row' }} mb={2} alignItems="center">
        <Typography variant={{ xs: 'h3', sm: 'h3' }} sx={{ color: '#1B5E20', fontWeight: 800,fontSize:"28px"}}>
          Production Center Scheme
        </Typography>
        <Button fullWidth={isMobile} variant="contained" startIcon={<Add />} onClick={() => { setFormData(getInitialFormState()); setOpen(true); }} sx={goldButtonStyle} size={isMobile ? "medium" : "small"}>
          Assign Scheme
        </Button>
      </Box>

      <Paper elevation={0} sx={{ overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        {initialLoading ? (
          <Box textAlign="center" p={5}><CircularProgress /></Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <DynamicTable data={tableData} columns={columns} loading={loading} onEdit={handleEditClick} onDelete={handleDeleteClick} />
          </Box>
        )}
      </Paper>

      <DynamicOffcanvas open={open} onClose={() => setOpen(false)} title={formData.id ? "Edit Assignment" : "Assign Scheme to Center"} width={offcanvasWidth}>
        <Box mt={2} sx={{ width: '100%' }}>
          <Grid container spacing={3} direction="column">
            <Grid item xs={12}>
              <Typography variant="body2" mb={1} fontWeight={600} sx={{ color: '#000' }}>Private Production Center</Typography>
              <Autocomplete fullWidth options={centers} getOptionLabel={(option) => option.name_of_production_centre || option.name || ''} value={formData.production_center_id} onChange={(e, newValue) => handleChange('production_center_id', newValue)} isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (<TextField {...params} label="Select Center" placeholder="Type to search..." variant="outlined" size="small" error={!!errors.production_center_id} helperText={errors.production_center_id} sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4caf50', borderWidth: 1.5 }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32 !important', borderWidth: 2 }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1b5e20', borderWidth: 2 } }, '& .MuiInputLabel-root': { color: '#000000', fontWeight: 500 }, '& .MuiInputLabel-root.Mui-focused': { color: '#000000' }} } />)}
                renderOption={(props, option) => (<Box component="li" {...props} key={option.id}><Typography variant="body2">{option.name_of_production_centre || option.name}</Typography><Chip label={option.production_type} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} color={option.production_type === 'private' ? 'success' : 'default'} /></Box>)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" mb={1} fontWeight={600} sx={{ color: '#000' }}>Scheme</Typography>
              <Autocomplete fullWidth options={availableSchemes} getOptionLabel={(option) => option.name || ''} value={formData.scheme_id} onChange={(e, newValue) => handleChange('scheme_id', newValue)} isOptionEqualToValue={(option, value) => option.id === value.id} noOptionsText={formData.production_center_id ? "All schemes already assigned to this center" : "Select a center first"}
                renderInput={(params) => (<TextField {...params} label="Select Scheme" placeholder="Type to search..." variant="outlined" size="small" error={!!errors.scheme_id} helperText={errors.scheme_id} sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4caf50', borderWidth: 1.5 }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2e7d32 !important', borderWidth: 2 }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1b5e20', borderWidth: 2 } }, '& .MuiInputLabel-root': { color: '#000000', fontWeight: 500 }, '& .MuiInputLabel-root.Mui-focused': { color: '#000000' }} } />)}
                renderOption={(props, option) => (<Box component="li" {...props} key={option.id}><Typography variant="body2">{option.name}</Typography>{option.percentage && (<Chip label={`${option.percentage}%`} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} color="primary" />)}</Box>)}
              />
            </Grid>
          </Grid>
          <Button onClick={handleSubmit} startIcon={<Save />} fullWidth variant="contained" sx={{ ...goldButtonStyle, mt: 5, width: '100%', py: 1.5 }} disabled={submitting}>
            {submitting ? "Saving..." : "Save Assignment"}
          </Button>
        </Box>
      </DynamicOffcanvas>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullScreen={isMobile} maxWidth="sm">
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove the mapping for <strong>{selectedRow?.center_name}</strong> and <strong>{selectedRow?.scheme_name}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} fullWidth={isMobile}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" startIcon={<DeleteIcon />} fullWidth={isMobile}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ maxWidth: { xs: '90%', sm: '500px' } }}>
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ width: '100%', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );  
};

export default ProductionCenterScheme;